import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  ApiActivityHierarchyEmissionsInventoryResponse,
  ApiCoreDataRequiredForV4,
  ApiListTransitionStoryGroupsResponse,
} from '../../../api/core/models';
import { DashboardAdditionalData } from '../../../feature/dashboard/model/dashboard-additional-data';
import { clone } from '../../../functions/rxjs-pipes/clone';
import { emitTruthyOnceAndComplete } from '../../../functions/rxjs-pipes/emit-truthy-once-and-complete';
import { filterDefined } from '../../../functions/rxjs-pipes/filter-defined';
import { PublishedBoardsModule } from '../../../store/published-boards/published-boards-modules';
import { publishedBoardsFacadeActions } from '../../../store/published-boards/published-boards.actions';
import {
  selectPublishedBoard,
  selectPublishedBoardData,
  selectPublishedBoardModuleData,
} from '../../../store/published-boards/published-boards.selectors';
import { PublishedBoardState } from '../../../store/published-boards/published-boards.types';
import {
  PublishedBoardV3Data,
  PublishedBoardV4_1Data,
  PublishedBoardV4Data,
} from '../../backend/backend-published-board.types';

type CastPublishedBoardData<T extends 'V3' | 'V4'> = T extends 'V3'
  ? Observable<PublishedBoardV3Data>
  : T extends 'V4'
    ? Observable<
        | PublishedBoardV4Data
        | (Omit<PublishedBoardV4Data, 'board'> & {
            data: PublishedBoardV4_1Data;
          })
        | (Omit<PublishedBoardV4Data, 'board'> & {
            data: PublishedBoardV4_1Data;
            transitionData: ApiListTransitionStoryGroupsResponse;
          })
      >
    : never;

type CastPublishedBoardModuleData<T extends PublishedBoardsModule> = T extends PublishedBoardsModule.CORE
  ? Observable<ApiCoreDataRequiredForV4>
  : T extends PublishedBoardsModule.CALCULATIONS
    ? Observable<DashboardAdditionalData>
    : T extends PublishedBoardsModule.EMISSIONS
      ? Observable<ApiActivityHierarchyEmissionsInventoryResponse>
      : T extends PublishedBoardsModule.TRANSITIONS
        ? Observable<any>
        : never;

/**
 * Facade service to interact with the published board.
 */
@Injectable({ providedIn: 'root' })
export class PublishedBoardsFacadeService {
  private _store = inject(Store);

  /**
   * @returns The data of the published board once. Can be V3 or V4.
   *
   * We have to cast the type because blobUrl that
   * we get from getPublishedBoard$ isn't typed.
   * So make sure you know it's V3 or V4 when using this.
   */
  getPublishedBoardData$<T extends 'V3' | 'V4'>(boardId: string): CastPublishedBoardData<T> {
    this._store.dispatch(publishedBoardsFacadeActions.loadPublishedBoardData({ boardId }));
    return <CastPublishedBoardData<T>>(
      this._store.select(selectPublishedBoardData(boardId)).pipe(emitTruthyOnceAndComplete, clone)
    );
  }

  /**
   * @returns The data of the module provided once.
   *
   * We have to cast the type because blobUrl that
   * we get from getPublishedBoardStatus$ isn't typed.
   * So make sure you know it's V3 or V4 when using this.
   */
  getPublishedBoardModule$<T extends PublishedBoardsModule>(
    boardId: string,
    module: PublishedBoardsModule
  ): CastPublishedBoardModuleData<T> {
    this._store.dispatch(publishedBoardsFacadeActions.loadPublishedBoardData({ boardId, module }));
    return <CastPublishedBoardModuleData<T>>(
      this._store.select(selectPublishedBoardModuleData(boardId, module)).pipe(emitTruthyOnceAndComplete, clone)
    );
  }

  /**
   * @returns The published board once.
   */
  getPublishedBoard$(boardId: string): Observable<PublishedBoardState | Error> {
    this._store.dispatch(publishedBoardsFacadeActions.loadPublishedBoard({ boardId }));
    return this._store.select(selectPublishedBoard(boardId)).pipe(emitTruthyOnceAndComplete);
  }

  /**
   * @returns The status of the published board, doesn't complete. Useful e.g. when changing board.
   */
  getPublishedBoardStatusStream$(boardId: string): Observable<PublishedBoardState | Error> {
    this._store.dispatch(publishedBoardsFacadeActions.loadPublishedBoard({ boardId }));
    return this._store.select(selectPublishedBoard(boardId)).pipe(filterDefined);
  }

  setPublishedStatus(boardId: string): void {
    this._store.dispatch(publishedBoardsFacadeActions.loadPublishedBoard({ boardId, forceLoad: true }));
  }
}
